/**
 * PublicVisitRequestPage — Visitors Registration Form (VRF).
 * URL: https://<app>/?vc=<vcQrHash>
 * No login. Company + site pre-filled and locked from QR scan.
 */
import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, Plus, Trash2, User, Car } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const S = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', borderHover: 'rgba(255,255,255,0.16)',
  text: '#e2e8f0', muted: '#64748b', subtle: '#94a3b8',
  green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', teal: '#009688',
};

const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

function emptyPerson() { return { fullName: '', nationality: '', idPassportNumber: '', address: '', phoneNumber: '', jobTitle: '' }; }
function emptyVehicle() { return { vehicleType: '', plateNumber: '' }; }

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: `1px solid ${S.border}`, background: S.elevated,
  color: S.text, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box',
};
const labelStyle = { fontSize: '11px', fontWeight: 600, color: S.muted, marginBottom: '5px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' };
const sectionTitle = { fontSize: '11px', fontWeight: 700, color: S.teal, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '12px' };

export default function PublicVisitRequestPage({ vcHash }) {
  const [phase, setPhase] = useState('loading'); // loading|form|done|error
  const [company, setCompany] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    arrivalDate: '', departureDate: '',
    visitReason: '', priority: 'NORMAL',
    sponsorDepartment: '', visitorCompanyEmail: '', visitRemark: '',
  });
  const [personnel, setPersonnel] = useState([emptyPerson()]);
  const [vehicles, setVehicles] = useState([emptyVehicle()]);

  useEffect(() => {
    if (!vcHash) { setError('No QR code provided. Please scan the company QR code to access this form.'); setPhase('error'); return; }
    fetch(`${API}/api/public/visitor-company/${vcHash}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setPhase('error'); return; }
        setCompany(data);
        setPhase('form');
      })
      .catch(() => { setError('Unable to reach the server. Check your connection.'); setPhase('error'); });
  }, [vcHash]);

  function updForm(key, val) { setForm(f => ({ ...f, [key]: val })); }
  function updPerson(idx, key, val) { setPersonnel(p => p.map((r, i) => i === idx ? { ...r, [key]: val } : r)); }
  function updVehicle(idx, key, val) { setVehicles(v => v.map((r, i) => i === idx ? { ...r, [key]: val } : r)); }
  function addPerson()  { setPersonnel(p => [...p, emptyPerson()]); }
  function remPerson(i) { setPersonnel(p => p.filter((_, x) => x !== i)); }
  function addVehicle() { setVehicles(v => [...v, emptyVehicle()]); }
  function remVehicle(i) { setVehicles(v => v.filter((_, x) => x !== i)); }

  function validate() {
    if (!form.arrivalDate)   return 'Arrival date is required';
    if (!form.departureDate) return 'Departure date is required';
    if (!form.visitReason)   return 'Visit reason is required';
    if (personnel.length === 0) return 'At least one person must be listed';
    for (const p of personnel) if (!p.fullName.trim()) return 'Each person must have a full name';
    return null;
  }

  function handleSubmit() {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    fetch(`${API}/api/public/visit-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vcQrHash: vcHash,
        ...form,
        personnel: personnel.filter(p => p.fullName.trim()),
        vehicles: vehicles.filter(v => v.vehicleType || v.plateNumber),
      }),
    })
      .then(r => r.json())
      .then(data => {
        setSubmitting(false);
        if (data.error) { setError(data.error); return; }
        setPhase('done');
      })
      .catch(() => { setSubmitting(false); setError('Network error. Please try again.'); });
  }

  const page = {
    minHeight: '100vh', background: S.bg, display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  };
  const header = {
    width: '100%', background: S.surface, borderBottom: `1px solid ${S.border}`,
    padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '10px',
  };

  if (phase === 'loading') return (
    <div style={page}>
      <div style={{ marginTop: '40vh', color: S.muted, fontSize: '14px', textAlign: 'center' }}>
        <Shield size={32} color={S.blue} style={{ display: 'block', margin: '0 auto 12px' }} />
        Loading…
      </div>
    </div>
  );

  if (phase === 'error') return (
    <div style={page}>
      <div style={header}><Shield size={20} color={S.blue} /><span style={{ fontSize: '14px', fontWeight: 700, color: S.text }}>SafeCheck — Visit Request</span></div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '360px' }}>
          <AlertTriangle size={40} color={S.amber} style={{ display: 'block', margin: '0 auto 16px' }} />
          <div style={{ fontSize: '16px', fontWeight: 700, color: S.text, marginBottom: '8px' }}>Unable to Load Form</div>
          <div style={{ fontSize: '14px', color: S.subtle }}>{error}</div>
        </div>
      </div>
    </div>
  );

  if (phase === 'done') return (
    <div style={page}>
      <div style={header}><Shield size={20} color={S.blue} /><span style={{ fontSize: '14px', fontWeight: 700, color: S.text }}>SafeCheck — Visit Request</span></div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '420px' }}>
          <CheckCircle size={64} color={S.green} style={{ display: 'block', margin: '0 auto 20px' }} />
          <div style={{ fontSize: '22px', fontWeight: 700, color: S.text, marginBottom: '10px' }}>Request Submitted</div>
          <div style={{ background: S.green + '22', border: `1px solid ${S.green}44`, borderRadius: '12px', padding: '16px', marginTop: '16px', fontSize: '14px', color: S.green, lineHeight: 1.6 }}>
            Your visit request for <strong>{company?.siteName}</strong> has been received.<br />
            You will be notified once it is reviewed.
          </div>
        </div>
      </div>
    </div>
  );

  // ── Form ──────────────────────────────────────────────────
  return (
    <div style={page}>
      <div style={header}>
        <Shield size={20} color={S.blue} />
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: S.text }}>SafeCheck</div>
          <div style={{ fontSize: '11px', color: S.muted }}>Visitor Registration Form</div>
        </div>
      </div>

      <div style={{ flex: 1, padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        {error && (
          <div style={{ background: S.red + '22', border: `1px solid ${S.red}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '20px', fontSize: '13px', color: S.red }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>

          {/* ── Left: General Info ── */}
          <div style={{ background: S.surface, border: `1px solid ${S.teal}44`, borderRadius: '12px', padding: '20px' }}>
            <div style={{ ...sectionTitle, borderBottom: `1px solid ${S.border}`, paddingBottom: '10px', marginBottom: '16px' }}>VR General Information</div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Site</label>
              <input style={{ ...inputStyle, background: S.bg, color: S.subtle, cursor: 'not-allowed' }} value={company?.siteName || ''} readOnly />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Arrival Date *</label>
                <input type="date" style={inputStyle} value={form.arrivalDate} onChange={e => updForm('arrivalDate', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Departure Date *</label>
                <input type="date" style={inputStyle} value={form.departureDate} onChange={e => updForm('departureDate', e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Visit Reason *</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }} value={form.visitReason} onChange={e => updForm('visitReason', e.target.value)} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Priority *</label>
                <select style={inputStyle} value={form.priority} onChange={e => updForm('priority', e.target.value)}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Sponsor Department</label>
                <input style={inputStyle} value={form.sponsorDepartment} onChange={e => updForm('sponsorDepartment', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Visitor Company Name</label>
                <input style={{ ...inputStyle, background: S.bg, color: S.subtle, cursor: 'not-allowed' }} value={company?.name || ''} readOnly />
              </div>
              <div>
                <label style={labelStyle}>Company Email</label>
                <input type="email" style={inputStyle} value={form.visitorCompanyEmail} onChange={e => updForm('visitorCompanyEmail', e.target.value)} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Visit Remark</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} value={form.visitRemark} onChange={e => updForm('visitRemark', e.target.value)} />
            </div>
          </div>

          {/* ── Right: Personnel + Vehicles ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Personnel table */}
            <div style={{ background: S.surface, border: `1px solid ${S.teal}44`, borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${S.border}`, paddingBottom: '10px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={14} color={S.teal} />
                  <span style={sectionTitle}>VR Personnel Information</span>
                </div>
                <button onClick={addPerson} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${S.teal}55`, background: S.teal + '22', color: S.teal, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Plus size={13} /> New
                </button>
              </div>

              {personnel.length === 0 ? (
                <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', padding: '20px 0' }}>No data to display</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr>
                        {['', 'Full Name', 'Nationality', 'ID/Passport', 'Phone', 'Job Title'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: S.subtle, fontWeight: 600, borderBottom: `1px solid ${S.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {personnel.map((p, i) => (
                        <tr key={i}>
                          <td style={{ padding: '6px 4px' }}>
                            <button onClick={() => remPerson(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.red, padding: '2px' }} title="Remove">
                              <Trash2 size={13} />
                            </button>
                          </td>
                          {[['fullName','Full Name *'],['nationality','Nationality'],['idPassportNumber','ID/Passport'],['phoneNumber','Phone'],['jobTitle','Job Title']].map(([k, ph]) => (
                            <td key={k} style={{ padding: '4px' }}>
                              <input style={{ ...inputStyle, padding: '6px 8px', minWidth: '90px' }} placeholder={ph} value={p[k]} onChange={e => updPerson(i, k, e.target.value)} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Vehicles table */}
            <div style={{ background: S.surface, border: `1px solid ${S.teal}44`, borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${S.border}`, paddingBottom: '10px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Car size={14} color={S.teal} />
                  <span style={sectionTitle}>VR Vehicle Information</span>
                </div>
                <button onClick={addVehicle} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${S.teal}55`, background: S.teal + '22', color: S.teal, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Plus size={13} /> New
                </button>
              </div>

              {vehicles.length === 0 ? (
                <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', padding: '20px 0' }}>No data to display</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      {['', 'Vehicle Type', 'Plate Number'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: S.subtle, fontWeight: 600, borderBottom: `1px solid ${S.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v, i) => (
                      <tr key={i}>
                        <td style={{ padding: '6px 4px' }}>
                          <button onClick={() => remVehicle(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.red, padding: '2px' }} title="Remove">
                            <Trash2 size={13} />
                          </button>
                        </td>
                        {[['vehicleType','Vehicle Type'],['plateNumber','Plate Number']].map(([k, ph]) => (
                          <td key={k} style={{ padding: '4px' }}>
                            <input style={{ ...inputStyle, padding: '6px 8px' }} placeholder={ph} value={v[k]} onChange={e => updVehicle(i, k, e.target.value)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingBottom: '40px' }}>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{ padding: '12px 28px', borderRadius: '10px', border: 'none', background: S.teal, color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Submitting…' : 'SAVE & SUBMIT'}
          </button>
          <button
            onClick={() => setPhase('error')}
            style={{ padding: '12px 20px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
